import { ModalWizardSteps, ModalWizzarStepsProvider } from '@/components/new'
import { ModalWizardTabsProvider } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { ModalWizardTabs } from '@/components/new/modalWizardTabs/ModalWizardTabs'
import DetailsExpTab from '@/containers/logistic/expedients/wizzardExpedient/tabs/DetailsExpTab'
import { IContainer } from '../interfaces/interfaces'
import DetailsContainerTab from './tabs/details/DetailsContainerTab'
import DetailsAdvancedContainerTab from './tabs/detailsAdvanced/DetailsAdvancedContainerTab'
import AseguramientoContainerTab from './tabs/aseguramiento/AseguramientoContainerTab'
import ProductsContainerDisplayTab from './tabs/products/ProductsContainerDisplayTab'
import { useContainerWizard } from './hooks/useContainerWizzard'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'
import ProductsContainerTab from './tabs/products/ProductsContainerTab'
import CostsContainerTab from './tabs/costs/CostsContainerTab'
import DocumentsContTab from './tabs/documents/DocumentsContTab'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createContainer } from '@/api/containersAPI'
import { toast } from 'react-toastify'
import DetailsGeneralContainerTab from './tabs/details/DetailsGeneralContainerTab'
import { useState } from 'react'

interface IProps {
  editContainer?: IContainer
  onClose: Function
  showState: boolean
}

export default function ExpedientWizard({
  editContainer,
  onClose,
  showState,
}: IProps) {
  const { currentStep, setCurrentStep, nextStep, prevStep } = useContainerWizard()
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState(0)
  
  const createContainerMutation = useMutation({
    mutationFn: createContainer,
    onSuccess: () => {
      toast.success('Contenedor creado exitosamente')
      queryClient.invalidateQueries({ queryKey: ['containers'] })
      onClose()
    },
    onError: (error: any) => {
      console.error('Error creating container:', error)
      toast.error(error?.response?.data?.message || 'Error al crear el contenedor')
    },
  })

  if (!editContainer)
    return (
      <ModalWizzarStepsProvider>
        <ModalWizardSteps
          isSending={createContainerMutation.isPending}
          currentStep={currentStep}
          isOpen={showState}
          onClose={() => {
            setCurrentStep(0)
            onClose()
            updateData({
              name: '',
              originId: 0,
              countryFromId: 0,
              destinyId: 0,
              categories: [],
              levanteNumber: '',
              trackingNumber: '',
              observations: '',
              documents: [],
              listCosts: [],
            })
          }}
          title={'Nuevo contenedor'}
          confirmOnClose={true}
          steps={[
            {
              name: 'Detalles generales',
              actionButtons: [
                {
                  label: 'Siguiente',
                  primaryAction: (data) => {
                    nextStep()
                  },
                },
              ],
              component: <DetailsGeneralContainerTab />,
            },
            {
              name: 'Documentación',
              actionButtons: [
                {
                  label: 'Anterior',
                  secondaryAction: () => {
                    prevStep()
                  },
                },
                {
                  label: 'Siguiente',
                  primaryAction: (data) => {
                    nextStep()
                  },
                },
              ],
              component: <DocumentsContTab />,
            },
            {
              name: 'Costos',
              actionButtons: [
                {
                  label: 'Anterior',
                  secondaryAction: () => {
                    prevStep()
                  },
                },
                {
                  label: 'Guardar',
                  primaryAction: (data) => {
                    // Obtener todos los datos del formulario
                    const formData = useContainerWizzardSteps.getState().data
                    // Preparar los datos para crear el contenedor
                    const containerData = {
                      name: formData.name,
                      originId: typeof formData.originId === 'object' && formData.originId !== null ? Number(formData.originId.id) : (formData.originId ?? null),
                      countryFromId: formData.countryFromId,
                      destinyId: typeof formData.destinyId === 'object' && formData.destinyId !== null ? Number(formData.destinyId.id) : (formData.destinyId ?? null),
                      categories: Array.isArray(formData.categories)
                        ? formData.categories.map((c) => typeof c === 'object' && c.id ? Number(c.id) : Number(c))
                        : [],
                      levanteNumber: formData.levanteNumber,
                      trackingNumber: formData.trackingNumber,
                      observations: formData.observations,
                      documents: formData.documents,
                      listCosts: formData.listCosts,
                      ownerCompanyId: formData.ownerCompany ? Number(formData.ownerCompany) : null,
                      importerCompanyId: formData.importerCompany ? Number(formData.importerCompany) : null,
                      transporterCompanyId: formData.transporterCompany ? Number(formData.transporterCompany) : null,
                      storageCompanyId: formData.storageCompany ? Number(formData.storageCompany) : null,
                      managerCompanyId: formData.logisticCompany ? Number(formData.logisticCompany) : null,
                      ...(formData.isRefrigered === true && { isRefrigered: true }),
                      ...(formData.isPalleted === true && { isPalleted: true }),
                      code: formData.code ?? null,
                      sealNumber: formData.sealNumber ?? null,
                      temperatureRange: formData.temperatureRange ?? null,
                      transportedBy: formData.transportedBy ?? null,
                      netWeight: formData.netWeight ?? null,
                      numberOfPallets: formData.numberOfPallets ?? null,
                    }
                
                    // Crear el contenedor
                    createContainerMutation.mutate(containerData)
                  },
                },
              ],
              component: <CostsContainerTab />,
            },
          ]}
        />
      </ModalWizzarStepsProvider>
    )
  else
    return (
      <ModalWizardTabsProvider>
        <ModalWizardTabs
          isOpen={!!editContainer}
          isSending={false}
          onClose={() => {
            onClose()
            updateData({
              name: '',
              origin: { id: 0, name: '' },
              destiny: { id: 0, name: '' },
              country: { id: 0, name: '' },
              destination: { id: 0, name: '' },
              category: { id: 0, name: '' },
              liftNumber: null,
              trackingNumber: null,
              description: '',
              costs: [],
              products: [],
              documents: [],
            })
          }}
          onTabChange={(idx) => setActiveTab(idx)}
          title={'Editar contenedor'}
          steps={[
            {
              name: 'Detalles',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingDetails ? 'Guardando...' : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (window.handleSaveDetails && !window.isSavingDetails) {
                      // @ts-ignore
                      window.handleSaveDetails()
                    }
                  },
                },
              ],
              component: (
                <DetailsContainerTab isEditing editContainer={editContainer} />
              ),
            },
            {
              name: 'Detalles avanzados',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingAdvancedDetails ? 'Guardando...' : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (window.handleSaveAdvancedDetails && !window.isSavingAdvancedDetails) {
                      // @ts-ignore
                      window.handleSaveAdvancedDetails()
                    }
                  },
                },
              ],
              component: (
                <DetailsAdvancedContainerTab isEditing editContainer={editContainer} />
              ),
            },
            {
              name: 'Aseguramiento',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingAseguramiento ? 'Guardando...' : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (window.handleSaveAseguramiento && !window.isSavingAseguramiento) {
                      // @ts-ignore
                      window.handleSaveAseguramiento()
                    }
                  },
                },
              ],
              component: <AseguramientoContainerTab isEditing editContainer={editContainer} />,
            },
            {
              name: 'Productos/Lote',
              actionButtons: [
                {
                  label: 'Guardar',
                  primaryAction: (data: any) => {},
                },
              ],
              component: <ProductsContainerTab isEditing editContainer={editContainer} />,
            },
            {
              name: 'Documentos',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingDocuments ? 'Guardando...' : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (window.handleSaveDocuments && !window.isSavingDocuments) {
                      // @ts-ignore
                      window.handleSaveDocuments()
                    }
                  },
                },
              ],
              component: <DocumentsContTab isEditing editContainer={editContainer} />,
            },
            {
              name: 'Costos',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingCosts ? 'Guardando...' : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (window.handleSaveCosts && !window.isSavingCosts) {
                      // @ts-ignore
                      window.handleSaveCosts()
                    }
                  },
                },
              ],
              component: <CostsContainerTab isEditing editContainer={editContainer} />,
            },
          ]}
        />
      </ModalWizardTabsProvider>
    )
}
